import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Edit, Trash2 } from "lucide-react";
import ConfirmActionDialog from "@/components/ConfirmActionDialog";
import { PageHeader, PageShell } from "@/components/PageShell";
import { PageEmptyState, PageLoadingState } from "@/components/PageStates";
import { handleArrowFieldKeyDown } from "@/lib/keyboardNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MasterKategoriPage = () => {
  const [kategori, setKategori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    nama_kategori: "",
    aktif: true,
  });
  const [currentId, setCurrentId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    description: "",
    confirmText: "Ya",
    destructive: false,
    onConfirm: null,
  });
  const kategoriNamaInputRef = useRef(null);
  const kategoriCancelButtonRef = useRef(null);
  const kategoriSubmitButtonRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/kategori`);
      setKategori(response.data);
    } catch (error) {
      toast.error("Gagal memuat data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setConfirmDialog({
      open: true,
      title: editMode ? "Konfirmasi Perbarui Kategori" : "Konfirmasi Simpan Kategori",
      description: editMode
        ? "Apakah Anda yakin ingin memperbarui data kategori ini?"
        : "Apakah Anda yakin ingin menyimpan data kategori ini?",
      confirmText: editMode ? "Ya, Perbarui" : "Ya, Simpan",
      destructive: false,
      onConfirm: async () => {
        try {
          if (editMode) {
            await axios.put(`${API}/kategori/${currentId}`, formData);
            toast.success("Kategori berhasil diupdate");
          } else {
            await axios.post(`${API}/kategori`, formData);
            toast.success("Kategori berhasil ditambahkan");
          }
          setDialogOpen(false);
          resetForm();
          fetchData();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Gagal menyimpan data");
        }
      },
    });
  };

  const handleEdit = (item) => {
    setFormData({
      nama_kategori: item.nama_kategori,
      aktif: item.aktif,
    });
    setCurrentId(item.kategori_id);
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    setConfirmDialog({
      open: true,
      title: "Konfirmasi Hapus Kategori",
      description: "Apakah Anda yakin ingin menghapus kategori ini? Tindakan ini tidak dapat dibatalkan.",
      confirmText: "Ya, Hapus",
      destructive: true,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/kategori/${id}`);
          toast.success("Kategori berhasil dihapus");
          fetchData();
        } catch (error) {
          toast.error("Gagal menghapus kategori");
        }
      },
    });
  };

  const resetForm = () => {
    setFormData({
      nama_kategori: "",
      aktif: true,
    });
    setEditMode(false);
    setCurrentId(null);
  };

  if (loading) {
    return <PageLoadingState label="Memuat master kategori" description="Menyiapkan daftar kategori aktif dan nonaktif." />;
  }

  return (
    <PageShell data-testid="master-kategori-page">
      <PageHeader
        eyebrow="Master Data"
        title="Master Kategori"
        description="Kelola kelompok barang agar proses pencarian, pelaporan, dan supplier mapping tetap rapi."
        actions={(
          <Button
            data-testid="add-kategori-button"
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            className="w-full sm:w-auto"
          >
            <Plus size={16} className="mr-2" />
            Tambah Kategori
          </Button>
        )}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {kategori.length === 0 ? (
          <div className="col-span-full">
            <PageEmptyState
              title="Belum ada kategori"
              description="Tambahkan kategori pertama untuk membantu pengelompokan barang dan supplier."
            />
          </div>
        ) : (
          kategori.map((item) => (
            <div
              key={item.kategori_id}
              data-testid={`kategori-card-${item.kategori_id}`}
              className="rounded-lg border bg-card p-6 shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-manrope font-bold text-lg">
                  {item.nama_kategori}
                </h3>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    item.aktif
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {item.aktif ? "Aktif" : "Nonaktif"}
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  data-testid={`edit-kategori-${item.kategori_id}`}
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(item)}
                  className="flex-1"
                >
                  <Edit size={16} className="mr-2" />
                  Edit
                </Button>
                <Button
                  data-testid={`delete-kategori-${item.kategori_id}`}
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(item.kategori_id)}
                >
                  <Trash2 size={16} className="text-destructive" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editMode ? "Edit Kategori" : "Tambah Kategori"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nama Kategori</Label>
              <Input
                ref={kategoriNamaInputRef}
                data-testid="form-nama-kategori"
                value={formData.nama_kategori}
                onChange={(e) =>
                  setFormData({ ...formData, nama_kategori: e.target.value })
                }
                placeholder="Contoh: Lauk Pauk, Sayuran"
                required
                onKeyDown={(event) =>
                  handleArrowFieldKeyDown(event, {
                    onLeft: () => kategoriCancelButtonRef.current?.focus(),
                    onRight: () => kategoriSubmitButtonRef.current?.focus(),
                  })
                }
              />
            </div>

            <DialogFooter>
              <Button
                ref={kategoriCancelButtonRef}
                type="button"
                variant="outline"
                onKeyDown={(event) =>
                  handleArrowFieldKeyDown(event, {
                    onRight: () => kategoriNamaInputRef.current?.focus(),
                  })
                }
                onClick={() => setDialogOpen(false)}
              >
                Batal
              </Button>
              <Button
                ref={kategoriSubmitButtonRef}
                data-testid="submit-kategori-button"
                type="submit"
                onKeyDown={(event) =>
                  handleArrowFieldKeyDown(event, {
                    onLeft: () => kategoriNamaInputRef.current?.focus(),
                  })
                }
              >
                {editMode ? "Update" : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <ConfirmActionDialog
        open={confirmDialog.open}
        onOpenChange={(open) =>
          setConfirmDialog((prev) => ({ ...prev, open, onConfirm: open ? prev.onConfirm : null }))
        }
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText={confirmDialog.confirmText}
        destructive={confirmDialog.destructive}
        onConfirm={async () => {
          if (confirmDialog.onConfirm) {
            await confirmDialog.onConfirm();
          }
          setConfirmDialog((prev) => ({ ...prev, open: false, onConfirm: null }));
        }}
      />
    </PageShell>
  );
};

export default MasterKategoriPage;
